/**
 * Ghost Verification Detail Page
 * Display basic information and PI License image
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Tabs,
  Tab,
  MenuItem,
  Snackbar,
  Alert,
  Breadcrumbs,
  Link,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { GhostVerificationService } from '../../../services/ghost/GhostVerificationService';
import type { GhostApplicationDetail } from '../../../types/ghostVerification';
import {
  Card,
  CardContent,
  PrimaryButton,
  TextField,
} from '../../../components/ui';
import { GhostApplicationInfoCard } from './components/GhostApplicationInfoCard';
import { OverviewTab } from './components/tabs/OverviewTab';
import { PILicenseTab } from './components/tabs/PILicenseTab';
import { DocumentsTab } from './components/tabs/DocumentsTab';
import { BondTab } from './components/tabs/BondTab';

const GhostVerificationDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [application, setApplication] = useState<GhostApplicationDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'pi-license' | 'documents' | 'bond'>('overview');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectError, setRejectError] = useState<string | null>(null);
  const [rejectAttempted, setRejectAttempted] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [selectedGhostTier, setSelectedGhostTier] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    const fetchApplicationDetail = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const detail = await GhostVerificationService.getApplicationDetail(id);
        setApplication(detail);
      } catch (error) {
        console.error('Failed to fetch application detail:', error);
        setApplication(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplicationDetail();
  }, [id]);

  const handleApproveClick = () => {
    if (!application?.ghostTier) {
      setSelectedGhostTier('Junior'); // Default to Junior
    } else {
      setSelectedGhostTier(application.ghostTier.toString());
    }
    setApproveDialogOpen(true);
  };

  const handleApproveCancel = () => {
    setApproveDialogOpen(false);
    setSelectedGhostTier('');
  };

  const handleApproveConfirm = async () => {
    if (!application || !selectedGhostTier || !id) return;
    try {
      setIsProcessing(true);
      setApproveDialogOpen(false);
      await GhostVerificationService.approveApplication(id, selectedGhostTier);
      // Refresh application data
      const detail = await GhostVerificationService.getApplicationDetail(id);
      setApplication(detail);
    } catch (error) {
      console.error('Failed to approve application:', error);
    } finally {
      setIsProcessing(false);
      setSelectedGhostTier('');
    }
  };

  const handleRejectClick = () => {
    setRejectDialogOpen(true);
    setRejectionReason('');
    setRejectError(null);
    setRejectAttempted(false);
  };

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
    setRejectionReason('');
    setRejectError(null);
    setRejectAttempted(false);
  };

  const handleReject = async () => {
    if (!application || !id) return;

    setRejectAttempted(true);
    if (!rejectionReason.trim()) {
      setRejectError('Rejection reason is required');
      return;
    }

    try {
      setIsProcessing(true);
      setRejectError(null);
      await GhostVerificationService.rejectApplication(id, rejectionReason.trim());
      // Refresh application data
      const detail = await GhostVerificationService.getApplicationDetail(id);
      setApplication(detail);
      setRejectDialogOpen(false);
      setRejectionReason('');
    } catch (error) {
      console.error('Failed to reject application:', error);
      setRejectError(error instanceof Error ? error.message : 'Failed to reject application');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRefreshApplication = async () => {
    if (!application || !id) return;
    try {
      const detail = await GhostVerificationService.getApplicationDetail(id);
      setApplication(detail);
    } catch (error) {
      console.error('Failed to refresh application:', error);
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: 'overview' | 'pi-license' | 'documents' | 'bond') => {
    setActiveTab(newValue);
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage(null);
  };


  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!application) {
    return (
      <MainLayout>
        <EmptyState
          title="Application Not Found"
          message="The application you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  const dialogActionButtonSx = {
    borderRadius: theme.shapes.borderRadius.md,
    padding: theme.spacing(1, 3),
    minWidth: 140,
  };

  const approveActionButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#2F7F66' : '#2E8B68',
    color: '#F5FFFB',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#3B9477' : '#277A5B',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  const rejectActionButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#A14F63' : '#B85A70',
    color: '#FFF8FA',
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#B35E74' : '#A44D62',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  const dialogCancelButtonSx = {
    ...dialogActionButtonSx,
    backgroundColor: theme.palette.mode === 'dark' ? '#2D3138' : '#EEF2F6',
    color: theme.palette.text.primary,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: theme.palette.mode === 'dark' ? '#363B43' : '#E4E9EF',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      backgroundColor: theme.palette.action.disabledBackground,
      boxShadow: 'none',
      color: theme.palette.action.disabled,
    },
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Ghost Verification: {application.applicantName}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: theme.spacing(2) }}>
          {application.verificationStatus === 'Pending' && (
            <>
              <PrimaryButton
                startIcon={<ApproveIcon />}
                onClick={handleApproveClick}
                disabled={isProcessing}
                sx={approveActionButtonSx}
              >
                Approve
              </PrimaryButton>
              <PrimaryButton
                startIcon={<RejectIcon />}
                onClick={handleRejectClick}
                disabled={isProcessing}
                sx={rejectActionButtonSx}
              >
                Reject
              </PrimaryButton>
            </>
          )}
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Basic Information */}
        <Grid item xs={12} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            <GhostApplicationInfoCard application={application} />
          </Box>
        </Grid>

        {/* Right Column - Tabs */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent noPadding>
              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: theme.customColors.tableBorder }}>
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      minHeight: 48,
                    },
                    '& .Mui-selected': {
                      color: theme.palette.primary.main,
                    },
                  }}
                >
                  <Tab label="Overview" value="overview" />
                  {(application?.type === 'Registration' || application?.type === 'PiLicense') && (
                    <Tab label="PI License" value="pi-license" />
                  )}
                  {(application?.type === 'Registration' || application?.type === 'BondInfo') && (
                    <Tab label="Bond" value="bond" />
                  )}
                </Tabs>
              </Box>

              {/* Tab Content */}
              <Box sx={{ padding: theme.spacing(3) }}>
                {activeTab === 'overview' && <OverviewTab application={application} />}
                {activeTab === 'pi-license' && <PILicenseTab application={application} />}
                {activeTab === 'documents' && <DocumentsTab application={application} />}
                {activeTab === 'bond' && (
                  <BondTab application={application} onUpdate={handleRefreshApplication} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>


      {/* Reject Application Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={handleRejectCancel}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Reject Ghost Verification
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            Please provide a reason for rejecting this verification application. This reason will be shared with the applicant.
          </Typography>
          {rejectError && (
            <Box
              sx={{
                padding: theme.spacing(1.5),
                marginBottom: theme.spacing(2),
                backgroundColor: theme.palette.error.light,
                color: theme.palette.error.contrastText,
                borderRadius: theme.shape.borderRadius,
                fontSize: '0.875rem',
              }}
            >
              {rejectError}
            </Box>
          )}
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Rejection Reason"
            placeholder="Please provide a reason for rejection (required)..."
            value={rejectionReason}
            onChange={(e) => {
              setRejectionReason(e.target.value);
              if (rejectError) {
                setRejectError(null);
              }
            }}
            required
            error={rejectAttempted && !rejectionReason.trim()}
            helperText={
              rejectAttempted && !rejectionReason.trim()
                ? 'Rejection reason is required'
                : ''
            }
            spellCheck={false}
            inputProps={{
              'data-gramm': 'false',
              'data-gramm_editor': 'false',
              'data-enable-grammarly': 'false'
            }}
            sx={{ marginTop: theme.spacing(1) }}
          />
        </DialogContent>
        <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
          <PrimaryButton
            onClick={handleRejectCancel}
            disabled={isProcessing}
            sx={{
              ...dialogCancelButtonSx,
              color: theme.palette.text.primary,
            }}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={handleReject}
            disabled={isProcessing || !rejectionReason.trim()}
            sx={rejectActionButtonSx}
          >
            {isProcessing ? 'Rejecting...' : 'Reject'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Approve Application Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={handleApproveCancel}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            overflowX: 'hidden',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Approve Ghost Verification
        </DialogTitle>
        <DialogContent sx={{ overflowX: 'hidden' }}>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              marginBottom: theme.spacing(2),
            }}
          >
            Please select the ghost tier for this approved application.
          </Typography>
          <TextField
            select
            fullWidth
            label="Ghost Tier"
            value={selectedGhostTier}
            onChange={(e) => setSelectedGhostTier(e.target.value)}
            required
            sx={{ marginTop: theme.spacing(1) }}
          >
            <MenuItem value="Junior">Junior</MenuItem>
            <MenuItem value="Senior">Senior</MenuItem>
            <MenuItem value="Elite">Elite</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ padding: theme.spacing(2, 3) }}>
          <PrimaryButton
            onClick={handleApproveCancel}
            disabled={isProcessing}
            sx={{
              ...dialogCancelButtonSx,
              color: theme.palette.text.primary,
            }}
          >
            Cancel
          </PrimaryButton>
          <PrimaryButton
            onClick={handleApproveConfirm}
            disabled={isProcessing || !selectedGhostTier}
            sx={approveActionButtonSx}
          >
            {isProcessing ? 'Approving...' : 'Approve'}
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Success Message Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={4000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccessMessage} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </MainLayout >
  );
};

export default GhostVerificationDetailPage;
